/**
 * @file 设置gasfee速率和滑点弹窗
 */
import { useState } from 'react';
import { Modal, BoxedSelect, Button, Input } from '@bfe/moly';
import { useFm } from '@web3/base-hooks';
import { FeeLevel, GasPriceDetail } from '@/types/common';
import BigNumber from 'bignumber.js';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { ethAddress } from '@/config/address';
import { formatUSDYield } from '@/utils/num';

const BoxedSelectGroup = BoxedSelect.Group;

import styles from './index.module.scss';

interface Props {
  show: boolean;
  slippage?: string;
  gasFeeLevel?: FeeLevel;
  gasFeeStore?: Record<FeeLevel, GasPriceDetail>;
  onConfirm: (slippage?: string, gasFeeLevel?: FeeLevel) => void;
  onClose: () => void;
}

export const SettingModal = (props: Props) => {
  const { show, gasFeeLevel, slippage, gasFeeStore, onConfirm, onClose } =
    props;
  const fm = useFm();
  const [selfGasFeeLevel, setSelfGasFeeLevel] = useState(gasFeeLevel);
  const [selfSlippage, setSelfSlippage] = useState(slippage);
  const { poolInfo } = usePoolInfoContext();
  const { coinInfoStore } = useCoinInfoContext();
  const [buttonIsDisabled, setButtonIsDisabled] = useState(false);

  const onChangeGasFee = (v: string) => {
    setSelfGasFeeLevel(v as FeeLevel);
  };

  const onChangeSlippage = (v: string) => {
    const numberReg = /^\d+(\.){0,1}(\d{0,4})?$/;
    const isDisabled = !(numberReg.test(v) && +v >= 0.1 && +v < 50);
    setButtonIsDisabled(isDisabled);

    setSelfSlippage(v);
  };

  const gasToken = coinInfoStore[`${poolInfo.chainCode}${ethAddress}`] || {};

  return (
    <Modal
      open={show}
      onClose={onClose}
      title={fm('advanced_setting')}
      width={450}
      footer={null}
      modal={false}
      overlayClassName={styles.overlay}
    >
      {gasFeeLevel && gasFeeStore?.middle && (
        <>
          <div>{fm('gas_fee')}</div>
          <BoxedSelectGroup value={selfGasFeeLevel} onChange={onChangeGasFee}>
            {Object.keys(gasFeeStore).map((key) => {
              const gasFeeInfo = gasFeeStore[key as FeeLevel];
              const gasAmount = BigNumber(gasFeeInfo.gasFeeMax).toFixed(6);
              return (
                <BoxedSelect
                  value={`${key}`}
                  key={key}
                  className={styles.boxedSelect}
                >
                  <div className={styles.gasCell}>
                    <div className={styles.gasTitle}>
                      {key === FeeLevel.Slow && fm('slow')}
                      {key === FeeLevel.Middle && fm('average')}
                      {key === FeeLevel.Fast && fm('fast')}
                    </div>
                    <div className={styles.gasDesc}>
                      <span className={styles.gasAmount}>
                        {gasAmount ? `${gasAmount}${gasToken.symbol}` : '--'}
                        &nbsp;
                      </span>
                      <span className={styles.gasPrice}>
                        {gasAmount
                          ? formatUSDYield(
                              BigNumber(gasAmount)
                                .times(gasToken.price)
                                .toString()
                            )
                          : '--'}
                      </span>
                    </div>
                  </div>
                </BoxedSelect>
              );
            })}
          </BoxedSelectGroup>
        </>
      )}
      {!!slippage && (
        <div className={styles.slippage}>
          <div>{fm('slippage_tolerance')}</div>
          <div className={styles.slippageContent}>
            {['0.5', '1', '3'].map((value) => {
              return (
                <Button
                  className={styles.slippageButton}
                  onClick={() => onChangeSlippage(value)}
                  variant={
                    selfSlippage === value ? 'secondaryBrand' : 'secondary'
                  }
                  key={value}
                >
                  {value}%
                </Button>
              );
            })}
            <Input
              placeholder="0.1 ~ 49.9%"
              defaultValue={slippage}
              type="number"
              step="0.1"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChangeSlippage(event.target.value)
              }
              variant={buttonIsDisabled && 'error'}
              numberControl={false}
            />
          </div>
        </div>
      )}

      <Button
        variant="primary"
        className={styles.save}
        disabled={buttonIsDisabled}
        onClick={() => {
          onClose();
          onConfirm(selfSlippage, selfGasFeeLevel);
        }}
      >
        {fm('save')}
      </Button>
    </Modal>
  );
};
